import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { PickList } from 'primereact/picklist';
import { InputText } from 'primereact/inputtext';
import { LovSecurityRolesDTO } from '@api/ringil-api.v2';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  value: string[];
  onChange: (ids: string[]) => void;
  loadSecurityRoles?: (onSuccess: ((data: LovSecurityRolesDTO[]) => void)) => void;
  isAdmin?: boolean;
}

const InputPickSecurityRole: React.FC<ComponentProps> = ({ value, onChange, loadSecurityRoles, isAdmin = false }) => {
  const { lovSecurityRolesV2 } = useContext(ApiContext);
  const { hasModule } = useContext(AuthenticatedUserContext);
  const { tr } = useTranslationLgs();

  const [securityRoles, setSecurityRoles] = useState<LovSecurityRolesDTO[]>([]);

  const [sourceSecurityRoles, setSourceSecurityRoles] = useState<LovSecurityRolesDTO[]>();
  const [sourceSelection, setSourceSelection] = useState<LovSecurityRolesDTO[]>([]);
  const [targetSelection, setTargetSelection] = useState<LovSecurityRolesDTO[]>([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    if (loadSecurityRoles) {
      loadSecurityRoles(d => {
        if (!ac.signal.aborted) {
          setSecurityRoles(d);
        }
      });
      return () => {
        ac.abort();
      };
    } else {
      lovSecurityRolesV2(d => {
        if (!ac.signal.aborted) {
          setSecurityRoles(d);
        }
      });
      return () => {
        ac.abort();
      };
    }
  }, []);

  useEffect(() => {
    setSourceSecurityRoles(
      securityRoles.filter(item => isAdmin || !item.isOnlyInternal)
        .filter(item => isAdmin || !item.isPrivate || value.includes(item.id))
        .filter(item => isAdmin || (item.companyModules ?? []).some(m => hasModule(m, null)) || value.includes(item.id))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    )
  }, [securityRoles, value]);

  const matches = (item: LovSecurityRolesDTO, filter: string) => {
    const needle = filter.trim().toLowerCase();
    if (needle.length === 0) return true;
    return (item.name?.toLowerCase().includes(needle) || item.descriptionKey?.toLowerCase().includes(needle)) ?? false;
  };

  const filterHeader = (label: string, filter: string, setFilter: (v: string) => void) => (
    <div className="flex flex-col gap-2">
      <span>{label}</span>
      <InputText
        value={filter}
        onChange={e => setFilter(e.target.value)}
        onClick={e => e.stopPropagation()}
        placeholder={tr('InputPickSecurityRole.search', 'Search role')}
        className="w-full"
      />
    </div>
  );

  return (
    <PickList
        source={sourceSecurityRoles?.filter(p => !value.includes(p.id) && matches(p, sourceFilter))}
        target={sourceSecurityRoles?.filter(p => value.includes(p.id) && matches(p, targetFilter))}
        sourceSelection={sourceSelection}
        onSourceSelectionChange={e => setSourceSelection(e.value.filter((item: LovSecurityRolesDTO) => isAdmin || (!item.isPrivate && !item.isOnlyInternal)))}
        targetSelection={targetSelection}
        onTargetSelectionChange={e => setTargetSelection(e.value.filter((item: LovSecurityRolesDTO) => isAdmin || (!item.isPrivate && !item.isOnlyInternal)))}
        onChange={v => {
          // Items filtered out of a list are absent from v.source/v.target, so they must keep their
          // current side. Rebuild the selection from `value`, updating only the currently visible items.
          const visibleIds = new Set<string>([...v.source, ...v.target].map((p: LovSecurityRolesDTO) => p.id));
          const movedTargetIds = new Set<string>(v.target.map((p: LovSecurityRolesDTO) => p.id));
          const nextIds = new Set<string>(value);
          securityRoles.forEach(item => {
            if (!visibleIds.has(item.id)) return; // filtered-out item keeps its current side
            if (!isAdmin && (item.isPrivate || item.isOnlyInternal)) return; // non-admins cannot change these
            if (movedTargetIds.has(item.id)) nextIds.add(item.id);
            else nextIds.delete(item.id);
          });
          const sorted = securityRoles.filter(p => nextIds.has(p.id)).slice().sort((a: LovSecurityRolesDTO, b: LovSecurityRolesDTO) => a.name.localeCompare(b.name));
          onChange(sorted.map((p: LovSecurityRolesDTO) => p.id));
        }}
        sourceHeader={filterHeader(tr('InputPickSecurityRole.available', 'Available'), sourceFilter, setSourceFilter)}
        targetHeader={filterHeader(tr('InputPickSecurityRole.selected', 'Selected'), targetFilter, setTargetFilter)}
        itemTemplate={(item: LovSecurityRolesDTO) => {
          return (
            <div
              className={clsx((!isAdmin && (item.isPrivate || item.isOnlyInternal)) ? 'opacity-50 pointer-events-none' : '')}>
              <div>{item.name}</div>
              <div className="text-sm text-content-secondary break-words w-[390px]">{item.descriptionKey}</div>
            </div>
          );
        }}
        showSourceControls={false}
        showTargetControls={false}
        dataKey={'id'}
      />
  );
};

export default InputPickSecurityRole;
